import React, { useEffect, useRef } from "react";
import { pickColor, hexToRgb } from "./internal/colorUtils";

export const key = "plasma-waves";

const DEFAULT_COLOR_A = "#1e1b4b";
const DEFAULT_COLOR_B = "#9333ea";
const DEFAULT_COLOR_C = "#22d3ee";

// Per-pixel sine interference (classic demoscene plasma) on the main canvas
// would cost width*height samples per frame — way too slow at full DPR.
//
// Trick: render the plasma at a tiny resolution into an offscreen buffer
// (using `putImageData` for raw pixel control), then draw that buffer
// upscaled onto the main canvas with smoothing on. The result looks like a
// true continuous plasma at full resolution but the per-frame cost is
// constant in the canvas size.
const LOW_W = 128;
const LOW_H = 80;
const TIME_STEP = 0.022;

export default function PlasmaWaves({ colors = [], style }) {
  const canvasRef = useRef(null);
  const offscreenRef = useRef(null);
  const rafRef = useRef(null);
  const colorsRef = useRef([DEFAULT_COLOR_A, DEFAULT_COLOR_B, DEFAULT_COLOR_C]);

  useEffect(() => {
    colorsRef.current = [
      pickColor(colors, 0, DEFAULT_COLOR_A),
      pickColor(colors, 1, DEFAULT_COLOR_B),
      pickColor(colors, 2, DEFAULT_COLOR_C),
    ];
  }, [colors]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const ctx = canvas.getContext("2d");
    if (!ctx) return undefined;

    // Offscreen low-res buffer that holds the plasma pixels.
    const off = document.createElement("canvas");
    off.width = LOW_W;
    off.height = LOW_H;
    const offCtx = off.getContext("2d");
    if (!offCtx) return undefined;
    offscreenRef.current = off;

    const imageData = offCtx.createImageData(LOW_W, LOW_H);
    const data = imageData.data;

    let width = 0;
    let height = 0;
    const dpr = typeof window !== "undefined" ? Math.min(window.devicePixelRatio || 1, 2) : 1;
    let t = 0;

    const resize = () => {
      const parent = canvas.parentElement;
      if (!parent) return;
      // Use clientWidth/Height (layout dims) so an ancestor `transform: scale(...)`
      // (e.g. PreviewDeviceStage) doesn't shrink the canvas and pin it to top-left.
      width = Math.max(1, parent.clientWidth || parent.offsetWidth || 0);
      height = Math.max(1, parent.clientHeight || parent.offsetHeight || 0);
      canvas.width = Math.floor(width * dpr);
      canvas.height = Math.floor(height * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";
    };

    const tick = () => {
      const [hexA, hexB, hexC] = colorsRef.current;
      const cA = hexToRgb(hexA) || { r: 0, g: 0, b: 0 };
      const cB = hexToRgb(hexB) || { r: 255, g: 0, b: 255 };
      const cC = hexToRgb(hexC) || { r: 0, g: 255, b: 255 };

      // Each pixel: sum a few sines on (x, y, t) — that's the plasma
      // value, normalised to 0..1. We then map it through a 3-stop palette:
      //   0   → A     (background)
      //   0.5 → B     (mid)
      //   1   → C     (highlights)
      // The double lerp gives a smooth gradient that includes all three
      // colors, even though the math behind it is just sines.
      let i = 0;
      for (let y = 0; y < LOW_H; y += 1) {
        for (let x = 0; x < LOW_W; x += 1) {
          const dx = x - LOW_W / 2;
          const dy = y - LOW_H / 2;
          const v =
            Math.sin(x * 0.10 + t) +
            Math.sin(y * 0.13 + t * 0.9) +
            Math.sin((x + y) * 0.08 + t * 0.7) +
            Math.sin(Math.sqrt(dx * dx + dy * dy) * 0.18 - t);
          // v is in roughly -4..4 → normalise to 0..1
          const n = (v + 4) / 8;

          let r;
          let g;
          let b;
          if (n < 0.5) {
            const k = n * 2;
            r = cA.r + (cB.r - cA.r) * k;
            g = cA.g + (cB.g - cA.g) * k;
            b = cA.b + (cB.b - cA.b) * k;
          } else {
            const k = (n - 0.5) * 2;
            r = cB.r + (cC.r - cB.r) * k;
            g = cB.g + (cC.g - cB.g) * k;
            b = cB.b + (cC.b - cB.b) * k;
          }

          data[i] = r;
          data[i + 1] = g;
          data[i + 2] = b;
          data[i + 3] = 255;
          i += 4;
        }
      }
      offCtx.putImageData(imageData, 0, 0);

      // Stretch the low-res plasma over the full canvas. Image smoothing
      // turns the chunky low-res pixels into a buttery gradient at any size.
      ctx.drawImage(off, 0, 0, width, height);

      t += TIME_STEP;
      rafRef.current = requestAnimationFrame(tick);
    };

    resize();
    rafRef.current = requestAnimationFrame(tick);

    let ro = null;
    if (typeof ResizeObserver !== "undefined" && canvas.parentElement) {
      ro = new ResizeObserver(resize);
      ro.observe(canvas.parentElement);
    } else if (typeof window !== "undefined") {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (ro) {
        try { ro.disconnect(); } catch { /* noop */ }
      } else if (typeof window !== "undefined") {
        window.removeEventListener("resize", resize);
      }
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        display: "block",
        pointerEvents: "none",
        ...style,
      }}
    />
  );
}
